/**
 * Follow-Up Questions Agent – Dubai Police 999 Emergency Assistant
 *
 * Asks clarifying questions one by one (as requested by intent_agent),
 * collects answers, and returns to intent_agent with the gathered answers
 * so it can make a confident classification.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

export function createFollowUpAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'followup_agent',
    name: 'Follow-Up Questions Agent',
    description: 'Asks targeted clarifying questions from the intent agent, collects answers, then returns to intent for final classification',

    initialPrompt: 'Look at the followup_questions in context. Ask the FIRST question now in the user\'s language. Wait for each answer. When done, call followup_complete.',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_intent', { target: { agentId: 'intent_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'intent_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      // ─── followup_complete ──────────────────────────────────────────────
      if (toolName === 'followup_complete') {
        const answersRaw: string = args.answers || '{}';

        // Parse the JSON answers string gracefully
        let answersObj: Record<string, string> = {};
        try {
          answersObj = JSON.parse(answersRaw);
        } catch {
          // If not valid JSON, store as plain text
          answersObj = { response: answersRaw };
        }

        console.log(
          `%c[FollowUpAgent] Follow-up complete. Returning to intent_agent with answers:`,
          'color: #9c27b0; font-weight: bold',
          answersObj
        );

        return {
          toolName, args,
          result: {
            success: true,
            message: 'Follow-up questions complete. Returning to intent agent for final classification.',
          },
          triggersTransition: true,
          transitionTo: 'intent_agent',
          transitionData: {
            // Pass answers back to intent_agent
            followupAnswers: answersObj,
            // Preserve original question and any partial agency detection
            pendingQuestion: context.transitionData.pendingQuestion,
            recommendedAgencies: context.transitionData.recommendedAgencies || [],
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
          },
        };
      }

      return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
    },

    onActivate: async (context: AgentContext) => {
      const questions = (context.transitionData as any).followupQuestions;
      console.log(
        '[FollowUpAgent] Activated — questions to ask:',
        questions || '(none in context)'
      );
    },
  };
}

export default createFollowUpAgent;
